import { createIqInitialState, type IqDisplayState } from '../domain/IqDisplayState.ts';
import type { NativeGameFrame } from 'shared/lib/tv-game/nativeFrame.ts';
import type { CalibNormMap } from '../domain/RemoteDisplayState.ts';
import { keypointsToCalibMapWithRefs, rawToGameNormForTargetAttack } from './targetAttackPoseNorm.ts';
import {
  computeRemoteViewLayout,
  defaultRemoteViewLayout,
  type RemoteViewLayout,
} from 'shared/lib/tv-game/nativeLayout.ts';
import { wristsAsHands, WRIST_MIN_CONFIDENCE } from 'shared/lib/tv-game/pose.ts';
import type { PoseKeypoint } from 'shared/lib/tv-game/pose.ts';
import type { BladeKey } from '../domain/GameState.ts';

/**
 * Android sends #RRGGBBAA; CSS / canvas accept #RRGGBBAA in modern browsers,
 * but to be safe we normalise to rgba() so older Smart-TV browsers handle it too.
 */
function androidColorToCss(hex: string): string {
  if (!/^#[0-9a-fA-F]{8}$/.test(hex)) return hex;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const a = parseInt(hex.slice(7, 9), 16) / 255;
  return `rgba(${r},${g},${b},${a.toFixed(3)})`;
}

const TRAIL_MAX_LENGTH = 20;

export interface IIqRenderer {
  renderIq(state: IqDisplayState): void;
}

export class IqDisplayEngine {
  private readonly state: IqDisplayState = createIqInitialState();

  private cachedNormRefWidth: number | undefined = undefined;
  private cachedNormRefHeight: number | undefined = undefined;
  private cachedFlipX = false;
  private cachedFlipY = false;
  private cachedAspectRatio: number | undefined = undefined;
  private canvasWidth = 1920;
  private canvasHeight = 1080;
  /** True once the native side has confirmed STARTED via IqTouchAnswerData. */
  private gameStartedByNative = false;

  constructor(
    private readonly renderer: IIqRenderer,
    canvas?: HTMLCanvasElement,
  ) {
    if (canvas) {
      this.canvasWidth = canvas.width;
      this.canvasHeight = canvas.height;
    }
  }

  setCanvasSize(w: number, h: number): void {
    this.canvasWidth = w;
    this.canvasHeight = h;
  }

  getPhase(): IqDisplayState['phase'] {
    return this.state.phase;
  }

  getState(): Readonly<IqDisplayState> {
    return this.state;
  }

  startGame(): void {
    this.state.phase = 'playing';
    this.state.score = 0;
    this.state.progress = '';
    this.state.timeLeft = null;
    this.state.question = '';
    this.state.answers = [];
    this.state.labels = [];
  }

  stopGame(): void {
    this.state.phase = 'lobby';
    this.state.calibNormPoints = new Map();
    this.state.answers = [];
    this.state.labels = [];
    this.state.bladeTrails.clear();
    this.cachedNormRefWidth = undefined;
    this.cachedNormRefHeight = undefined;
    this.cachedAspectRatio = undefined;
    this.cachedFlipX = false;
    this.cachedFlipY = false;
    this.gameStartedByNative = false;
  }

  /** Called for KeypointsDataMultiplayer frames (parsed NativeGameFrame). */
  onCalibFrame(playerId: string, frame: NativeGameFrame): void {
    if (frame.normRefWidth !== undefined) this.cachedNormRefWidth = frame.normRefWidth;
    if (frame.normRefHeight !== undefined) this.cachedNormRefHeight = frame.normRefHeight;
    if (frame.flipX !== undefined) this.cachedFlipX = frame.flipX;
    if (frame.flipY !== undefined) this.cachedFlipY = frame.flipY;
    if (frame.aspectRatio !== undefined) this.cachedAspectRatio = frame.aspectRatio;

    const W = this.canvasWidth;
    const H = this.canvasHeight;
    const layout = this.cachedAspectRatio !== undefined
      ? computeRemoteViewLayout(W, H, this.cachedAspectRatio, undefined, { useNativeAspectRatio: true })
      : defaultRemoteViewLayout(W, H);
    this.state.remoteView = layout;

    if (this.gameStartedByNative) {
      // Native confirmed game is running — keep the phase, just feed hand trails.
      if (frame.keypoints.length > 0) {
        this.applyKeypointsToTrails(playerId, frame.keypoints, layout);
      }
      return;
    }

    this.state.phase = 'calibrating';
    this.state.calibNormPoints = keypointsToCalibMapWithRefs(
      frame.keypoints,
      this.cachedNormRefWidth,
      this.cachedNormRefHeight,
      this.cachedFlipX,
      this.cachedFlipY,
    );

    const rawFrame = frame as unknown as Record<string, unknown>;
    if (typeof rawFrame['detectionProgress'] === 'number') {
      this.state.detectionProgress = rawFrame['detectionProgress'] as number;
    }
  }

  /** Called for IqTouchAnswerData frames (raw JSON). */
  onGameFrame(playerId: string, frame: unknown): void {
    if (!frame || typeof frame !== 'object') return;
    const raw = frame as Record<string, unknown>;
    const gs = raw['gameState'] as string | undefined;
    if (gs === 'GAME_OVER') {
      this.state.phase = 'gameover';
      this.gameStartedByNative = false;
    } else if (gs === 'PAUSED') {
      this.state.phase = 'paused';
    } else if (gs === 'STARTED') {
      this.state.phase = 'playing';
      this.gameStartedByNative = true;
    } else if (gs === 'PREPARING') {
      // PREPARING without a parsed NativeGameFrame — stay in calibrating if already there
      if (this.state.phase !== 'calibrating') this.state.phase = 'calibrating';
      return;
    }

    if (typeof raw['progress'] === 'string') this.state.progress = raw['progress'] as string;
    if (typeof raw['timeLeft'] === 'number') this.state.timeLeft = raw['timeLeft'] as number;
    if (typeof raw['answerTextSize'] === 'number') this.state.answerTextSize = raw['answerTextSize'] as number;
    if (typeof raw['question'] === 'string') this.state.question = raw['question'] as string;

    const p1 = raw['player1'];
    if (p1 && typeof p1 === 'object') {
      const p1r = p1 as Record<string, unknown>;
      const score = p1r['score'];
      if (typeof score === 'number') this.state.score = score;
      if (Array.isArray(p1r['keypoints'])) {
        const layout = this.state.remoteView ?? defaultRemoteViewLayout(this.canvasWidth, this.canvasHeight);
        this.applyKeypointsToTrails(playerId, p1r['keypoints'] as PoseKeypoint[], layout);
      }
    }

    const answers = raw['answers'];
    if (Array.isArray(answers)) {
      this.state.answers = answers.map((a: Record<string, unknown>) => ({
        id: String(a['id'] ?? ''),
        nx: Number(a['x'] ?? 0),
        ny: Number(a['y'] ?? 0),
        nw: Number(a['width'] ?? 0),
        nh: Number(a['height'] ?? 0),
        text: String(a['text'] ?? ''),
        backgroundColor: androidColorToCss(String(a['backgroundColor'] ?? '#4caf50FF')),
      }));
    }

    const labels = raw['labels'];
    if (Array.isArray(labels)) {
      this.state.labels = labels.map((l: Record<string, unknown>) => ({
        text: String(l['text'] ?? ''),
        nx: Number(l['x'] ?? 0),
        ny: Number(l['y'] ?? 0),
        color: androidColorToCss(String(l['color'] ?? '#ffffffff')),
        alpha: Number(l['alpha'] ?? 1),
      }));
    }
  }

  private applyKeypointsToTrails(
    playerId: string,
    keypoints: readonly PoseKeypoint[],
    layout: RemoteViewLayout,
  ): void {
    const hands = wristsAsHands([...keypoints]);
    const present = new Set<BladeKey>();
    const now = Date.now();

    hands.forEach(({ hand, x, y, confidence }) => {
      if (typeof confidence === 'number' && confidence < WRIST_MIN_CONFIDENCE) return;
      if (x === 0 && y === 0) return;

      const key: BladeKey = `${playerId}_${hand}`;
      present.add(key);

      if (!this.state.bladeTrails.has(key)) this.state.bladeTrails.set(key, []);
      const trail = this.state.bladeTrails.get(key)!;
      const norm = rawToGameNormForTargetAttack(x, y, this.cachedNormRefWidth, this.cachedNormRefHeight, this.cachedFlipX, this.cachedFlipY);
      const px = layout.offsetX + norm.nx * layout.gameWidth;
      const py = layout.offsetY + norm.ny * layout.gameHeight;
      trail.push({ x: px, y: py, t: now });
      if (trail.length > TRAIL_MAX_LENGTH) trail.shift();
    });

    this.state.bladeTrails.forEach((trail, key) => {
      if (!key.startsWith(`${playerId}_`)) return;
      if (!present.has(key as BladeKey)) trail.length = 0;
    });
  }

  tick(): void {
    this.renderer.renderIq(this.state);
  }
}
